import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import {
  AlertCircle,
  Beaker,
  Calculator,
  Check,
  ChevronDown,
  ChevronUp,
  Droplets,
  ExternalLink,
  Loader2,
  Scale,
  X,
} from 'lucide-react';
import { useState, type JSX } from 'react';
import type {
  FormulaCalculationStep,
  FormulaCalculatorCategory,
  FormulaCalculatorResult,
  FormulaCriteriaBreakdown,
} from '../../contracts';

interface FormulaCalculatorBlockProps {
  formulaName: string;
  formulaExpression?: string; // Actual mathematical expression
  formulaDisplay?: string; // Human-readable formula
  category: FormulaCalculatorCategory;
  status: 'calculating' | 'completed' | 'error';
  variables?: Record<string, number | string | boolean>;
  result?: FormulaCalculatorResult;
  sourceUrl?: string;
  sourceName?: string;
  validationReference?: string;
  criteriaBreakdown?: FormulaCriteriaBreakdown[];
  calculationSteps?: FormulaCalculationStep[];
  warnings?: string[];
  error?: string;
  className?: string;
}

// Category configuration for visual styling
// Uses valid semantic tokens from src/styles/colors/semanticColors.ts
// Note: labels are translation keys, resolved at runtime via getCategoryLabel
const CATEGORY_CONFIG: Record<
  FormulaCalculatorCategory,
  { icon: typeof Calculator; colorClass: string; bgClass: string; labelKey: string }
> = {
  scoring: {
    icon: Calculator,
    colorClass: 'text-text-link',
    bgClass: 'bg-validation-info-muted',
    labelKey: 'chat.formulaCalculator.categories.clinicalScore',
  },
  dosing: {
    icon: Droplets,
    colorClass: 'text-validation-info',
    bgClass: 'bg-validation-info-muted',
    labelKey: 'chat.formulaCalculator.categories.dosing',
  },
  lab: {
    icon: Beaker,
    colorClass: 'text-accent-forest',
    bgClass: 'bg-validation-success-muted',
    labelKey: 'chat.formulaCalculator.categories.labCalculation',
  },
  conversion: {
    icon: Scale,
    colorClass: 'text-text-secondary',
    bgClass: 'bg-surface-hover',
    labelKey: 'chat.formulaCalculator.categories.conversion',
  },
  general_math: {
    icon: Calculator,
    colorClass: 'text-text-secondary',
    bgClass: 'bg-surface-hover',
    labelKey: 'chat.formulaCalculator.categories.calculation',
  },
};

// Risk level styling - text and background colors for result display
const RISK_LEVEL_CONFIG: Record<
  string,
  { textClass: string; bgClass: string; borderClass: string }
> = {
  low: {
    textClass: 'text-validation-success',
    bgClass: 'bg-validation-success-muted',
    borderClass: 'border-validation-success-border',
  },
  moderate: {
    textClass: 'text-validation-warning',
    bgClass: 'bg-validation-warning-muted',
    borderClass: 'border-validation-warning-border',
  },
  high: {
    textClass: 'text-validation-destructive',
    bgClass: 'bg-validation-destructive-muted',
    borderClass: 'border-validation-destructive-border',
  },
  critical: {
    textClass: 'text-validation-destructive font-semibold',
    bgClass: 'bg-validation-destructive-muted',
    borderClass: 'border-validation-destructive-border',
  },
};

// Default styling for results without risk level
const DEFAULT_RESULT_CONFIG = {
  textClass: 'text-text-secondary',
  bgClass: 'bg-surface',
  borderClass: 'border-border',
};

// Boolean value badge component
const BooleanBadge = ({
  value,
  yesLabel,
  noLabel,
}: {
  value: boolean;
  yesLabel: string;
  noLabel: string;
}) => {
  if (value) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-validation-success/10 px-1.5 py-0.5 text-xs font-medium text-validation-success">
        <Check className="size-3" />
        {yesLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-surface-hover px-1.5 py-0.5 text-xs text-text-tertiary">
      <X className="size-3" />
      {noLabel}
    </span>
  );
};

/**
 * FormulaCalculatorBlock - Displays dynamic formula calculator results
 *
 * EVI-620: This component displays results from the formula_calculator tool,
 * which uses evidence_search to find formulas dynamically.
 *
 * Visual states:
 * - CALCULATING: Shows loading spinner
 * - COMPLETED: Shows formula, result, criteria breakdown, calculation steps, and source
 * - ERROR: Shows error message
 */
export const FormulaCalculatorBlock = ({
  formulaName,
  formulaExpression,
  formulaDisplay,
  category,
  status,
  variables,
  result,
  sourceUrl,
  sourceName,
  validationReference,
  criteriaBreakdown,
  calculationSteps,
  warnings,
  error,
  className,
}: FormulaCalculatorBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTechnicalFormula, setShowTechnicalFormula] = useState(false);
  const config = CATEGORY_CONFIG[category] ?? CATEGORY_CONFIG.general_math;
  const IconComponent = config.icon;
  const categoryLabel = t(config.labelKey as TranslationKey);

  // Calculating state with spinner
  if (status === 'calculating') {
    return (
      <div
        data-testid="formula-calculator-block"
        data-status="calculating"
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-background-secondary p-3 duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface-hover">
          <Loader2 className="size-5 animate-spin text-text-secondary" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {formulaName}
          </TypographyP2>
          <TypographyP2 className="text-text-tertiary">
            {t('chat.formulaCalculator.calculating')}
          </TypographyP2>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        data-testid="formula-calculator-block"
        data-status="error"
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-3 duration-300 animate-in fade-in',
          className
        )}
      >
        <FramedIcon size="md" state="error" icon={<AlertCircle />} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {formulaName}
          </TypographyP2>
          <TypographyP2 className="text-validation-destructive">
            {error ?? t('chat.formulaCalculator.calculationFailed')}
          </TypographyP2>
        </div>
      </div>
    );
  }

  const hasVariables = variables && Object.keys(variables).length > 0;
  const hasCriteriaBreakdown = criteriaBreakdown && criteriaBreakdown.length > 0;
  const hasCalculationSteps = calculationSteps && calculationSteps.length > 0;
  const hasSource = sourceUrl || sourceName || validationReference;

  // Completed state with result
  return (
    <div
      data-testid="formula-calculator-block"
      data-status="completed"
      className={cn(
        'flex w-full flex-col gap-3 rounded-lg border border-border bg-background-secondary p-4 duration-300 animate-in fade-in',
        className
      )}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-md',
              config.bgClass
            )}
          >
            <IconComponent className={cn('size-5', config.colorClass)} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <TypographyP2 className="font-medium text-text-primary">
              {formulaName}
            </TypographyP2>
            <TypographyCaption className="text-text-tertiary">
              {categoryLabel}
            </TypographyCaption>
          </div>
        </div>

        {/* Source link */}
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-text-link transition-colors hover:bg-validation-info-muted"
          >
            {sourceName || t('chat.formulaCalculator.source')}
            <ExternalLink className="size-3" />
          </a>
        )}
      </div>

      {/* Formula display */}
      {(formulaDisplay || formulaExpression) && (
        <div className="rounded-md bg-surface p-3">
          <div className="flex items-center justify-between">
            <TypographyCaption className="font-medium text-text-tertiary">
              {t('chat.formulaCalculator.formula')}
            </TypographyCaption>
            {formulaExpression && (
              <button
                type="button"
                onClick={() => setShowTechnicalFormula(!showTechnicalFormula)}
                className="text-xs text-text-link hover:underline"
              >
                {showTechnicalFormula
                  ? t('chat.formulaCalculator.hideExpression')
                  : t('chat.formulaCalculator.showExpression')}
              </button>
            )}
          </div>
          {formulaDisplay && (
            <TypographyP2 className="mt-1 font-mono text-sm text-text-secondary">
              {formulaDisplay}
            </TypographyP2>
          )}
          {showTechnicalFormula && formulaExpression && (
            <div className="mt-2 rounded border border-border bg-background-secondary p-2 animate-in fade-in slide-in-from-top-1">
              <TypographyCaption className="mb-1 text-text-tertiary">
                {t('chat.formulaCalculator.technicalExpression')}
              </TypographyCaption>
              <code className="block whitespace-pre-wrap break-all font-mono text-xs text-text-secondary">
                {formulaExpression}
              </code>
            </div>
          )}
        </div>
      )}

      {/* Criteria breakdown for scoring tools */}
      {hasCriteriaBreakdown && (
        <div className="rounded-md bg-surface p-3">
          <TypographyCaption className="mb-2 font-medium text-text-tertiary">
            {t('chat.formulaCalculator.criteria')}
          </TypographyCaption>
          <div className="flex flex-col gap-1.5">
            {criteriaBreakdown.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {item.present ? (
                    <Check className="size-4 text-validation-success" />
                  ) : (
                    <X className="size-4 text-text-tertiary" />
                  )}
                  <TypographyCaption
                    className={cn(
                      item.present ? 'text-text-primary' : 'text-text-tertiary'
                    )}
                  >
                    {item.criterion}
                  </TypographyCaption>
                </div>
                <TypographyCaption
                  className={cn(
                    'font-medium',
                    item.points > 0 ? 'text-validation-success' : 'text-text-tertiary'
                  )}
                >
                  {item.points > 0 ? `+${item.points}` : item.points}
                </TypographyCaption>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calculation steps for multi-step calculations */}
      {hasCalculationSteps && (
        <div className="rounded-md bg-surface p-3">
          <TypographyCaption className="mb-2 font-medium text-text-tertiary">
            {t('chat.formulaCalculator.calculationSteps')}
          </TypographyCaption>
          <div className="flex flex-col gap-1.5">
            {calculationSteps.map((step, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2">
                <TypographyCaption className="text-text-secondary">
                  {step.step}: {step.calculation}
                </TypographyCaption>
                <TypographyCaption className="font-medium text-text-primary">
                  = {step.result}
                </TypographyCaption>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Result display */}
      {result &&
        (() => {
          const riskConfig = result.risk_level
            ? (RISK_LEVEL_CONFIG[result.risk_level] ?? DEFAULT_RESULT_CONFIG)
            : DEFAULT_RESULT_CONFIG;

          return (
            <div
              className={cn(
                'flex flex-col gap-2 rounded-md border p-3',
                riskConfig.bgClass,
                riskConfig.borderClass
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="font-body text-2xl font-semibold text-text-primary">
                  {typeof result.value === 'number'
                    ? Number.isInteger(result.value)
                      ? result.value
                      : result.value.toFixed(2)
                    : result.value}
                </span>
                {result.unit && (
                  <span className="font-body text-sm text-text-secondary">
                    {result.unit}
                  </span>
                )}
              </div>

              {result.interpretation && (
                <TypographyP2 className={riskConfig.textClass}>
                  {result.interpretation}
                </TypographyP2>
              )}
            </div>
          );
        })()}

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {warnings.map((warning, idx) => (
            <div
              key={idx}
              className="flex items-start gap-2 rounded-md border border-validation-warning-border bg-validation-warning-muted px-3 py-2"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-validation-warning" />
              <TypographyCaption className="text-text-primary">
                {warning}
              </TypographyCaption>
            </div>
          ))}
        </div>
      )}

      {/* Source attribution */}
      {hasSource && (
        <div className="border-t border-border pt-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {sourceName && !sourceUrl && (
              <TypographyCaption className="text-text-tertiary">
                {t('chat.formulaCalculator.sourceLabel')} {sourceName}
              </TypographyCaption>
            )}
            {validationReference && (
              <TypographyCaption className="italic text-text-tertiary">
                {t('chat.formulaCalculator.refLabel')} {validationReference}
              </TypographyCaption>
            )}
          </div>
        </div>
      )}

      {/* Expandable input details */}
      {hasVariables && (
        <div className="border-t border-border pt-2">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex w-full items-center justify-between rounded-md p-1 text-left transition-colors hover:bg-surface-hover"
          >
            <TypographyCaption className="font-medium text-text-tertiary">
              {isExpanded
                ? t('chat.formulaCalculator.hideInputs')
                : t('chat.formulaCalculator.showInputs')}
            </TypographyCaption>
            {isExpanded ? (
              <ChevronUp className="size-4 text-text-tertiary" />
            ) : (
              <ChevronDown className="size-4 text-text-tertiary" />
            )}
          </button>

          {isExpanded && (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 rounded-md bg-surface p-3 animate-in fade-in slide-in-from-top-1">
              {Object.entries(variables).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <TypographyCaption className="text-text-tertiary">
                    {key.replace(/_/g, ' ')}
                  </TypographyCaption>
                  {typeof value === 'boolean' ? (
                    <BooleanBadge
                      value={value}
                      yesLabel={t('chat.formulaCalculator.yes')}
                      noLabel={t('chat.formulaCalculator.no')}
                    />
                  ) : (
                    <TypographyCaption className="font-medium text-text-secondary">
                      {String(value)}
                    </TypographyCaption>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
